import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Loader2, Sparkles, ArrowRight, Eye, EyeOff } from "lucide-react";
import { devWarn } from "@/lib/devLog";

const REMEMBER_KEY = "crm_remember_me_v1";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Remember Me pre-fills only the email; passwords are never stored.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(REMEMBER_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.email) setEmail(saved.email);
      if (saved.password) localStorage.setItem(REMEMBER_KEY, JSON.stringify({ email: saved.email }));
      setRemember(true);
    } catch (e) { devWarn("Login/rememberMe.read", e); }
  }, []);

  if (user && user.id) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const u = await login(email, password);
      // Persist ONLY after a successful login so we never store bad credentials.
      if (remember) {
        try { localStorage.setItem(REMEMBER_KEY, JSON.stringify({ email })); }
        catch (e) { devWarn("Login/rememberMe.write", e); }
      } else {
        try { localStorage.removeItem(REMEMBER_KEY); }
        catch (e) { devWarn("Login/rememberMe.clear", e); }
      }
      nav(u.role === "admin" ? "/admin" : "/app", { replace: true });
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="grain relative flex min-h-screen bg-background">
      {/* Left: Hero */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden border-r border-border p-12 lg:flex">
        <div className="dot-grid absolute inset-0 opacity-40" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Sparkles className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">Northline CRM</div>
            <div className="mono text-xs text-muted-foreground">v.1.0 — Sales OS</div>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <div className="label-eyebrow">The operating system</div>
          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter text-foreground">
            Close leads
            <br />
            <span className="text-primary">faster.</span> Follow
            <br /> up smarter.
          </h1>
          <p className="max-w-md text-base text-muted-foreground">
            Auto-assign incoming leads to online agents. One-tap call, one-tap WhatsApp, and a follow-up engine that never sleeps.
          </p>
          <div className="mono grid max-w-md grid-cols-3 gap-4 border-t border-border pt-6 text-xs text-muted-foreground">
            <div>
              <div className="text-2xl font-bold text-foreground">1-tap</div>
              call &amp; WhatsApp
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">6</div>
              message templates
            </div>
            <div>
              <div className="text-2xl font-bold text-foreground">Auto</div>
              round-robin routing
            </div>
          </div>
        </div>

        <div className="mono relative z-10 text-xs text-muted-foreground">
          © {new Date().getFullYear()} Northline. All systems operational.
        </div>
      </div>

      {/* Right: Form */}
      <div className="relative flex w-full flex-col lg:w-1/2">
        <div className="flex items-center justify-between p-6">
          <div className="flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="font-display text-base font-bold">Northline CRM</div>
          </div>
          <div className="flex-1" />
          <ThemeToggle />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 pb-16">
          <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6 animate-fade-up">
            <div className="space-y-2">
              <div className="label-eyebrow">Sign in</div>
              <h2 className="font-display text-3xl font-bold tracking-tight">Welcome back.</h2>
              <p className="text-sm text-muted-foreground">
                Use your CRM credentials to continue.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    data-testid="login-password-toggle"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground tap-lift"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground select-none">
                <input
                  type="checkbox"
                  data-testid="login-remember-checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  className="h-4 w-4 rounded border-border accent-primary"
                />
                Remember me on this device
              </label>
            </div>

            {err && (
              <div data-testid="login-error" className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {err}
              </div>
            )}

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="group h-11 w-full rounded-full text-sm font-semibold tap-lift"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                <>Sign in <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" /></>
              )}
            </Button>

            <div className="mono text-center text-[10px] uppercase tracking-widest text-muted-foreground">
              Ask your admin for access
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
